import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { Calendar } from '../../types/calendar/calendar.type';
import { CalendarEvent } from '../../types/calendar/calendar-event.type';
import { DateHelper } from '../../helpers/date.helper';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { PublicHoliday } from '../../types/generated.types';

@Injectable({ providedIn: 'root' })
export class CalendarApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/calendar`;
  }

  async getCalendars(): Promise<Calendar[]> {
    return await this.get<Calendar[]>('');
  }

  async getEvents(calendarId: string, from: Date, to: Date, tenantId?: string): Promise<CalendarEvent[]> {
    const events = await this.get<CalendarEvent[]>(`${calendarId}/events/`, { from: DateHelper.formatApiDateTime(from), to: DateHelper.formatApiDateTime(to), tenantId: tenantId ?? undefined });
    ApiConversionHelper.convertDates(events, ['start', 'end']);
    return events;
  }

  async getEvent(eventId: string, startDate: Date): Promise<CalendarEvent> {
    const event = await this.get<CalendarEvent>(`events/${eventId}`, { startDate: DateHelper.formatApiDateTime(startDate) });
    ApiConversionHelper.convertDates([event], ['start', 'end']);
    return event;
  }

  async getPublicHolidays(from: Date, to: Date): Promise<PublicHoliday[]> {
    const holidays = await this.get<PublicHoliday[]>('holidays', { from: DateHelper.formatApiDateTime(from), to: DateHelper.formatApiDateTime(to) });
    ApiConversionHelper.convertDates(holidays, ['date']);
    return holidays;
  }

  async createEvent(event: CalendarEvent): Promise<void> {
    await this.post('events', event);
  }

  async updateEvent(event: CalendarEvent, forDate: Date = null, fromDate: Date = null): Promise<void> {
    await this.put('events', event, {
      forDate: forDate ? DateHelper.formatApiDateOnly(forDate) : null,
      fromDate: fromDate ? DateHelper.formatApiDateOnly(fromDate) : null,
    });
  }

  async deleteEvent(eventId: string, forDate: Date = null, fromDate: Date = null): Promise<void> {
    await this.delete(`events/${eventId}`, {
      forDate: forDate ? DateHelper.formatApiDateOnly(forDate) : null,
      fromDate: fromDate ? DateHelper.formatApiDateOnly(fromDate) : null,
    });
  }

  async setIgnoredPublicHoliday(englishName: string, countryCode: string, isIgnored: boolean): Promise<void> {
    await this.patch('holidays/is-ignored', { englishName, countryCode, isIgnored });
  }
}
